import PropTypes from "prop-types";
import * as React from "react";
import { STATUS, isRetryable } from "../../domain/mediaUpload";
import useMediaUpload from "../../hooks/useMediaUpload";
import { customerRecorderConfig } from "../../hooks/videoRecorderDefaults";
import { retryMedia } from "../../services/mediaUpload";
import MediaCaptureDialog from "../MediaCapture/MediaCaptureDialog";

const UPLOAD_TYPE = "customer_video_message";

/**
 * A fan recording a message for the talent to watch while signing. The dialog
 * closes once the recording has reached us — the transcode that follows needs
 * nothing from this page, and the addon row reports how it ends.
 */
const descriptionFor = ({ influencerName, isReplacing }) =>
  isReplacing
    ? `Record a new message for ${influencerName}. It replaces the one you sent once it has finished processing.`
    : `Send a video message to ${influencerName} that they can watch while signing your order!`;

function VideoMessageModal({
  autographPurchaseId,
  influencerName,
  isReplacing = false,
  onSent,
  onClose,
}) {
  const { upload, state: uploadState } = useMediaUpload();

  const handleSubmit = async (blob, recorderState) => {
    if (!blob) return;

    try {
      await upload(blob, {
        uploadType: UPLOAD_TYPE,
        referenceId: autographPurchaseId,
        mirrorVideo: !recorderState.isUploadedFile,
        mimeType: blob.type,
      });
    } catch {
      // The dialog reports it from uploadState; the throw is for callers that
      // await this, and there are none here.
    }
  };

  /* Closing on `queued` rather than `complete` is the point of the flow: the
     bytes are ours, so the fan is free to go. */
  React.useEffect(() => {
    if (uploadState.status === STATUS.QUEUED) {
      onSent();
    }
  }, [uploadState.status, onSent]);

  return (
    <MediaCaptureDialog
      title={isReplacing ? "Re-record video message" : "Record video message"}
      description={descriptionFor({ influencerName, isReplacing })}
      recorderConfig={customerRecorderConfig}
      uploadState={uploadState}
      uploadType={UPLOAD_TYPE}
      submitLabel="Send"
      canUpload={false}
      canCloseWhileSettling={false}
      onSubmit={handleSubmit}
      onClose={onClose}
      onRetrySend={
        isRetryable(uploadState)
          ? () => retryMedia(uploadState.mediaUploadId)
          : undefined
      }
    />
  );
}

VideoMessageModal.propTypes = {
  autographPurchaseId: PropTypes.number.isRequired,
  influencerName: PropTypes.string.isRequired,
  isReplacing: PropTypes.bool,
  onSent: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VideoMessageModal;
